// src/components/ViewAllProducts.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaTrashAlt, FaEdit, FaSearch } from "react-icons/fa";

const ViewAllProducts = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(
          "https://goldenhides.in/api/product/admin/all"
        );
        const data = resp.data?.products || [];
        setAllProducts(data);
        setFiltered(data);
      } catch (err) {
        console.error("Failed to fetch products", err);
        toast.error("Failed to load products", { position: "top-center" });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const term = (q || "").toLowerCase();
    if (!term) {
      setFiltered(allProducts);
      return;
    }
    setFiltered(
      allProducts.filter((p) =>
        `${p.title} ${p.description} ${p.category?.title} ${p.supplier?.firstName} ${p.supplier?.lastName}`
          .toLowerCase()
          .includes(term)
      )
    );
  }, [q, allProducts]);

  const confirmAndDelete = (id) => {
    if (!window.confirm("Delete this product?")) return;
    deleteProduct(id);
  };

  const deleteProduct = (productId) => {
    fetch("https://goldenhides.in/api/product/delete?productId=" + productId, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.success) {
          toast.success(res.responseMessage || "Deleted successfully", {
            position: "top-center",
          });
          setFiltered((prev) => prev.filter((p) => p.id !== productId));
          setAllProducts((prev) => prev.filter((p) => p.id !== productId));
        } else
          toast.error(res.responseMessage || "Delete failed", {
            position: "top-center",
          });
      })
      .catch(() => toast.error("Server error", { position: "top-center" }));
  };

  const updateProduct = (p) => navigate("/admin/product/update", { state: p });

  // navigate to product details, using category route if available
  const goToProduct = (p) => {
    const cid = p.category?.id ?? "";
    navigate(`/product/${p.id}/category/${cid}`);
  };

  return (
    <div className="container mt-4">
      <div
        className="card form-card shadow-lg custom-bg"
        style={{ borderRadius: 10 }}
      >
        {/* Header */}
        <div
          className="card-header d-flex align-items-center justify-content-between bg-color custom-bg-text"
          style={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}
        >
          <div>
            <h3 className="mb-0">All Products</h3>
            <small className="text-muted">
              Manage your inventory efficiently.
            </small>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div style={{ position: "relative" }}>
              <FaSearch
                style={{
                  position: "absolute",
                  left: 10,
                  top: 9,
                  color: "#999",
                }}
              />
              <input
                type="search"
                placeholder="Search by title, category, or supplier"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="form-control"
                style={{ paddingLeft: 34, width: 360, height: 36 }}
              />
            </div>

            <button
              className="btn btn-sm bg-color custom-bg-text"
              onClick={() => navigate("/admin/product/add")}
            >
              Add Product
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="card-body" style={{ minHeight: 360 }}>
          {loading ? (
            <div className="text-center py-5 text-muted">
              Loading products...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No products found.
            </div>
          ) : (
            <div className="row g-4">
              {filtered.map((p) => {
                // profit = selling - purchase
                const selling = Number(p.price || 0);
                const purchase = Number(p.purchasePrice || 0);
                const profit = Number((selling - purchase).toFixed(2));

                return (
                  <div className="col-xl-3 col-lg-4 col-md-6" key={p.id}>
                    <div
                      className="card h-100 shadow-sm"
                      style={{ borderRadius: 10 }}
                    >
                      {/* Image box - fixed height, centered, maintain aspect ratio */}
                      <div
                        onClick={() => goToProduct(p)}
                        style={{
                          height: 200,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          cursor: "pointer",
                          background: "#fff",
                        }}
                      >
                        <img
                          src={`https://goldenhides.in/api/product/${p.imageName}`}
                          alt={p.title}
                          style={{
                            maxHeight: "100%",
                            width: "auto",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </div>
                      <div className="card-body d-flex flex-column">
                        <div
                          style={{ cursor: "pointer" }}
                          onClick={() => goToProduct(p)}
                          title={p.title}
                        >
                          <h6 className="fw-bold mb-1" style={{ fontSize: 15 }}>
                            {p.title}
                          </h6>
                          <p className="text-muted small mb-1">
                            {p.category?.title || "-"}
                          </p>
                        </div>

                        <p
                          className="small text-muted mb-2"
                          style={{
                            overflow: "hidden",
                            height: 40,
                            textOverflow: "ellipsis",
                          }}
                        >
                          {p.description}
                        </p>

                        <div className="mt-auto">
                          <div className="d-flex justify-content-between small text-muted mb-2">
                            <div>Stock: {p.quantity}</div>
                            <div>Supplied: {p.suppliedQuantity}</div>
                          </div>

                          <div
                            className="d-flex justify-content-between mb-2"
                            style={{ fontSize: 14 }}
                          >
                            <div className="text-muted">
                              Purchase: ₹{purchase.toFixed(2)}
                            </div>
                            <div className="text-muted">
                              Selling: ₹{selling.toFixed(2)}
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div
                              style={{
                                fontWeight: 700,
                                color: profit >= 0 ? "#138a3e" : "#d32f2f",
                              }}
                            >
                              {profit >= 0 ? "Profit: " : "Loss: "}₹
                              {Math.abs(profit).toFixed(2)}
                            </div>

                            <div>
                              <button
                                onClick={() => updateProduct(p)}
                                className="btn btn-sm btn-outline-primary me-2"
                                title="Edit"
                                style={{ minWidth: 36 }}
                              >
                                <FaEdit />
                              </button>

                              <button
                                onClick={() => confirmAndDelete(p.id)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete"
                                style={{ minWidth: 36 }}
                              >
                                <FaTrashAlt />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>{" "}
                      {/* card-body */}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewAllProducts;
